import { useState } from "react";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import type { DiaDeVentas } from "./api";

/**
 * Catorce dias, una barra por dia.
 *
 * Barras y no un area con degradado. Un area suaviza y sugiere continuidad,
 * y una tienda de veinte pedidos al dia no es continua: vende el sabado y no
 * vende el martes, y eso es justo lo que hay que poder ver. La barra tambien
 * responde a la pregunta que se hace de verdad —"¿cuanto vendi el jueves?"—
 * mientras que un area solo deja estimar.
 *
 * SVG a mano en vez de una libreria de graficos: son cuarenta lineas contra
 * cien kilobytes que alguien tendria que descargar en Lima con datos moviles.
 */
export function SalesChart({
  dias,
  currency,
  timeZone,
}: {
  dias: DiaDeVentas[];
  currency: string;
  timeZone: string;
}) {
  const [encima, setEncima] = useState<number | null>(null);

  const maximo = Math.max(...dias.map((d) => d.total), 1);
  const total = dias.reduce((s, d) => s + d.total, 0);
  const conVentas = dias.filter((d) => d.total > 0).length;

  const hoy = new Date().toLocaleDateString("en-CA", { timeZone });

  const etiquetaDia = (iso: string) => {
    // Se construye a mediodia para que el cambio de huso no corra el dia.
    const f = new Date(`${iso}T12:00:00`);
    return {
      corto: f.toLocaleDateString("es-PE", { weekday: "narrow" }),
      largo: f.toLocaleDateString("es-PE", { weekday: "short", day: "numeric", month: "short" }),
    };
  };

  const activo = encima !== null ? dias[encima] : null;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <header className="flex items-baseline justify-between gap-3">
        <span className="text-mark font-mono uppercase text-ink-3">Últimos 14 días</span>
        <span className="tnum font-mono text-meta text-ink-2">
          {formatMoney(total, currency)}
        </span>
      </header>

      {total === 0 ? (
        <p className="py-7 text-center text-ui text-ink-2">
          Todavía no hay ventas en estas dos semanas.
        </p>
      ) : (
        <>
          <div
            className="mt-4 flex h-28 items-end gap-1"
            onMouseLeave={() => setEncima(null)}
          >
            {dias.map((d, i) => {
              // Un dia con ventas nunca se dibuja como una raya invisible: el
              // 4% de suelo lo distingue de un dia sin nada.
              const alto = d.total === 0 ? 0 : Math.max(4, (d.total / maximo) * 100);
              const esHoy = d.dia === hoy;

              return (
                <button
                  key={d.dia}
                  type="button"
                  onMouseEnter={() => setEncima(i)}
                  onFocus={() => setEncima(i)}
                  onBlur={() => setEncima(null)}
                  aria-label={`${etiquetaDia(d.dia).largo}: ${formatMoney(d.total, currency)}, ${d.pedidos} pedidos`}
                  className="group flex h-full flex-1 flex-col justify-end rounded-sm outline-none"
                >
                  <span
                    style={{ height: `${alto}%` }}
                    className={cn(
                      "w-full rounded-sm transition-colors",
                      d.total === 0
                        ? "h-px bg-line"
                        : esHoy
                          ? "bg-accent"
                          : "bg-accent/45 group-hover:bg-accent group-focus-visible:bg-accent",
                    )}
                  />
                </button>
              );
            })}
          </div>

          <div className="mt-1.5 flex gap-1">
            {dias.map((d, i) => (
              <span
                key={d.dia}
                className={cn(
                  "flex-1 text-center font-mono text-mark uppercase",
                  d.dia === hoy ? "text-accent" : "text-ink-3",
                  encima === i && "text-ink",
                )}
              >
                {etiquetaDia(d.dia).corto}
              </span>
            ))}
          </div>

          {/* La lectura del dia señalado ocupa un sitio fijo: si apareciera y
              desapareciera, el resto de la tarjeta daria saltos. */}
          <p className="mt-3 h-4 text-meta text-ink-2">
            {activo ? (
              <>
                <span className="capitalize">{etiquetaDia(activo.dia).largo}</span>
                {" · "}
                <span className="tnum font-medium text-ink">
                  {formatMoney(activo.total, currency)}
                </span>
                {activo.pedidos > 0 && (
                  <>
                    {" · "}
                    {activo.pedidos} {activo.pedidos === 1 ? "pedido" : "pedidos"}
                  </>
                )}
              </>
            ) : (
              `Vendiste ${conVentas} de los últimos 14 días.`
            )}
          </p>
        </>
      )}
    </div>
  );
}
